import { useFormContext } from "react-hook-form";
import { FormSchemaType } from "@/features/hmo-consultation/schema";
import {
  FormDescription,
  FormLabel,
  FormMessage,
  FormField,
  FormItem,
  FormControl,
} from "@/components/ui/form";
import { leadwayStatesAndLgas, statesAndLgas } from "@/lib/states-and-lgas";
import { Input } from "@/components/ui/input";
import { CustomSelect } from "@/components/ui/custom-select";
import { useHMOStore } from "@/features/hmo-consultation/hooks/use-hmo-store";
import { leadwayIds } from "@/lib/utils";

export const AddressInput = () => {
  const form = useFormContext<FormSchemaType>();
  const hasOptionalLgaAddressFeature = useHMOStore(
    (s) => s.providerFeatures?.hasOptionalLgaAddressFeature,
  );
  const appState = useHMOStore();
  const providerId = appState?.partnerInfo?.providerId;

  const isLeadwayProvider = !!providerId && leadwayIds.includes(providerId);
  const selectedState = form.watch("state");
  const isLagosSelected = selectedState?.toLowerCase() === "lagos";

  const shouldHideLgaDropdown = !!hasOptionalLgaAddressFeature && isLagosSelected;
  const lgaSource = isLeadwayProvider ? leadwayStatesAndLgas : statesAndLgas;
  const lgaOptions = Array.from(new Set(lgaSource[selectedState || ""] || [])).map(
    (lga) => ({
      value: lga,
      label: lga,
    }),
  );

  return (
    <div className="space-y-3">
      <div>
        <FormLabel required>Address</FormLabel>
        <FormDescription>
          NB: This address will be used to deliver any prescription (drugs) from
          the doctor.
        </FormDescription>
      </div>

      {/* Street Address Field */}
      <FormField
        control={form.control}
        name="street"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <Input {...field} placeholder="Enter Address" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* State Field */}
      <FormField
        control={form.control}
        name="state"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <CustomSelect
                {...field}
                placeholder="Select State"
                options={Object.keys(isLeadwayProvider ? leadwayStatesAndLgas :
                   statesAndLgas).map((state) => ({
                  value: state,
                  label: state,
                }))}
                onChange={(e) => {
                  field.onChange(e.target.value);

                  // Reset LGA when state changes
                  form.setValue("lga", "");

                  // Reset pharmacy when state changes
                  form.setValue("pharmacyCode", "");
                  form.setValue("pharmacyAddress", "");
                  form.setValue("pharmacyName", "");
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* LGA Field - Hidden when Lagos is selected for excluded partners */}
      {!shouldHideLgaDropdown && (
        <FormField
          control={form.control}
          name="lga"
          render={({ field }) => {
            return (
              <FormItem>
                <FormControl>
                  <CustomSelect
                    key={selectedState || "no-state"}
                    {...field}
                    value={field.value || ""}
                    placeholder="Select LGA"
                    disabled={!selectedState}
                    options={lgaOptions}
                    onChange={(e) => {
                      field.onChange(e.target.value);

                      // Reset pharmacy when LGA changes
                      form.setValue("pharmacyCode", "");
                      form.setValue("pharmacyAddress", "");
                      form.setValue("pharmacyName", "");
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      )}
    </div>
  );
};
